import React, { useState, useCallback } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Breadcrumbs,
  Link as MUILink,
  Paper,
  Stack,
  Alert,
  Skeleton,
  Chip,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  Collapse,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import {
  Home,
  Terminal,
  Refresh,
  Search,
  FilterList,
  ExpandMore,
  ExpandLess,
  Error as ErrorIcon,
  Warning,
  Info,
  BugReport,
} from '@mui/icons-material';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useSystemLogs,
  useSystemLogsSummary,
  type SystemLogEntry,
  type SystemLogsFilters,
} from '@/hooks/queries/admin/useSystemLogs';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SOURCE_TYPES = ['pod', 'job', 'event', 'cronjob', 'deployment'] as const;
const LOG_LEVELS = ['debug', 'info', 'warn', 'error', 'fatal'] as const;
const NAMESPACES = ['equip', 'monitoring', 'istio-system', 'cert-manager', 'kube-system'] as const;

const LOG_LEVEL_CONFIG: Record<
  string,
  { color: 'default' | 'info' | 'warning' | 'error' | 'success'; icon: React.ReactElement }
> = {
  error: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
  fatal: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
  warn: { color: 'warning', icon: <Warning fontSize="small" /> },
  info: { color: 'info', icon: <Info fontSize="small" /> },
  debug: { color: 'default', icon: <BugReport fontSize="small" /> },
};

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

const SummaryCard: React.FC<{ label: string; value: number | string; color?: string }> = ({
  label,
  value,
  color,
}) => (
  <Card variant="outlined" sx={{ flex: 1, minWidth: 140 }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={600} color={color ?? 'text.primary'}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const LogLevelChip: React.FC<{ level: string | null }> = ({ level }) => {
  if (!level) return <Chip label="—" size="small" variant="outlined" />;
  const config = LOG_LEVEL_CONFIG[level] ?? {
    color: 'default' as const,
    icon: <Info fontSize="small" />,
  };
  return <Chip label={level} size="small" color={config.color} icon={config.icon} />;
};

const formatTimestamp = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export const SystemLogsPage: React.FC = () => {
  usePageTitle('System Logs');
  const navigate = useNavigate();

  // Filter state
  const [filters, setFilters] = useState<SystemLogsFilters>({ limit: 25, offset: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Queries
  const { data: logsData, isLoading, error, refetch } = useSystemLogs(filters);
  const { data: summary, isLoading: summaryLoading } = useSystemLogsSummary(filters);

  // Pagination
  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setFilters((prev) => ({ ...prev, offset: newPage * (prev.limit ?? 25) }));
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, limit: parseInt(event.target.value, 10), offset: 0 }));
  }, []);

  // Search
  const handleSearch = useCallback(() => {
    setFilters((prev) => ({ ...prev, search: searchInput || undefined, offset: 0 }));
  }, [searchInput]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch],
  );

  // Filter changes
  const updateFilter = useCallback((key: keyof SystemLogsFilters, value: string | string[]) => {
    const resolved = Array.isArray(value) ? value.join(',') : value;
    setFilters((prev) => ({ ...prev, [key]: resolved || undefined, offset: 0 }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ limit: 25, offset: 0 });
    setSearchInput('');
  }, []);

  const currentPage = Math.floor((filters.offset ?? 0) / (filters.limit ?? 25));

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MUILink
          underline="hover"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </MUILink>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <Terminal sx={{ mr: 0.5 }} fontSize="small" />
          System Logs
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            System Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View logs from all pods, jobs, events, and cluster activity
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => refetch()}>
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      {summaryLoading ? (
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} variant="rounded" width="16%" height={80} />
          ))}
        </Stack>
      ) : summary ? (
        <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
          <SummaryCard label="Total" value={summary.summary.total_entries} />
          <SummaryCard label="Fatal" value={summary.summary.fatal_count} color="error.dark" />
          <SummaryCard label="Errors" value={summary.summary.error_count} color="error.main" />
          <SummaryCard
            label="Warnings"
            value={summary.summary.warning_count}
            color="warning.main"
          />
          <SummaryCard label="Info" value={summary.summary.info_count} color="info.main" />
          <SummaryCard label="Debug" value={summary.summary.debug_count} color="text.secondary" />
        </Stack>
      ) : null}

      {/* Search & Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search log messages..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>
          <Tooltip title="Toggle filters">
            <IconButton
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              aria-label="Toggle filters"
            >
              <FilterList />
              {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Tooltip>
        </Stack>

        <Collapse in={filtersExpanded}>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Source Type</InputLabel>
              <Select
                multiple
                value={filters.source_type ? filters.source_type.split(',') : []}
                input={<OutlinedInput label="Source Type" />}
                onChange={(e) => updateFilter('source_type', e.target.value as string[])}
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                {SOURCE_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    <Checkbox
                      checked={(filters.source_type?.split(',') ?? []).includes(t)}
                      size="small"
                    />
                    <ListItemText primary={t} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Log Level</InputLabel>
              <Select
                multiple
                value={filters.log_level ? filters.log_level.split(',') : []}
                input={<OutlinedInput label="Log Level" />}
                onChange={(e) => updateFilter('log_level', e.target.value as string[])}
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                {LOG_LEVELS.map((l) => (
                  <MenuItem key={l} value={l}>
                    <Checkbox
                      checked={(filters.log_level?.split(',') ?? []).includes(l)}
                      size="small"
                    />
                    <ListItemText primary={l} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Namespace</InputLabel>
              <Select
                value={filters.namespace ?? ''}
                input={<OutlinedInput label="Namespace" />}
                onChange={(e) => updateFilter('namespace', e.target.value as string)}
              >
                <MenuItem value="">All</MenuItem>
                {NAMESPACES.map((ns) => (
                  <MenuItem key={ns} value={ns}>
                    {ns}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Source Name"
              value={filters.source_name ?? ''}
              onChange={(e) => updateFilter('source_name', e.target.value)}
              sx={{ minWidth: 160 }}
            />

            <TextField
              size="small"
              label="From"
              type="datetime-local"
              value={filters.from ?? ''}
              onChange={(e) => updateFilter('from', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />

            <TextField
              size="small"
              label="To"
              type="datetime-local"
              value={filters.to ?? ''}
              onChange={(e) => updateFilter('to', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />

            <Button variant="text" onClick={clearFilters}>
              Clear Filters
            </Button>
          </Stack>
        </Collapse>
      </Paper>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load system logs. {(error as Error).message}
        </Alert>
      )}

      {/* Log Table */}
      <Paper>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 160 }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 80 }}>Level</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 90 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 180 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 90 }}>Namespace</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : logsData?.data.map((log: SystemLogEntry) => (
                    <TableRow
                      key={log.id}
                      hover
                      sx={{
                        backgroundColor:
                          log.log_level === 'error' || log.log_level === 'fatal'
                            ? 'error.50'
                            : log.log_level === 'warn'
                              ? 'warning.50'
                              : undefined,
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {formatTimestamp(log.ingested_at)}
                      </TableCell>
                      <TableCell>
                        <LogLevelChip level={log.log_level} />
                      </TableCell>
                      <TableCell>
                        <Chip label={log.source_type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          maxWidth: 180,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title={log.source_name}>
                          <span>{log.source_name}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{log.namespace}</TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title={log.message}>
                          <span>{log.message}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              {!isLoading && logsData?.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No log entries found. Logs are collected every 5 minutes from cluster pods,
                      jobs, and events.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {logsData && (
          <TablePagination
            component="div"
            count={logsData.pagination.total}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={filters.limit ?? 25}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        )}
      </Paper>
    </Container>
  );
};
